import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Constants } from '@app/shared/constant/constants';
import { Messages } from '@app/shared/constant/messages';
import { LoaderService, RequestService, SessionManagerService, ToastService } from '@app/shared/services';
import { CustomValidator } from '@app/shared/validator/custom.validator';
import { BsModalService } from 'ngx-bootstrap/modal';
import { CanvasService } from '../../services/canvas.service';
import { ToolbarService } from '../../services/toolbar.service';
import { NgIf } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FocusOnInitDirective } from '@app/shared/directive/modal-focus.directive';
import { TabbingService } from '@app/shared/services/tabbing.service';
import { DynamicTabIndexDirective } from '@app/shared/directive/dynamic-tab-index.directive';
import { timeout, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

@Component({
  selector: 'app-email-pdf-popup',
  templateUrl: './email-pdf-popup.component.html',
  styleUrls: ['./email-pdf-popup.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgIf, TranslateModule, FocusOnInitDirective, DynamicTabIndexDirective],
})
export class EmailPdfPopupComponent implements OnInit, OnDestroy {
  @Input() fileName = '';
  formValues!: UntypedFormGroup;
  CommonConfig: any = Messages;

  constructor(
    private readonly modalService: BsModalService,
    private fb: UntypedFormBuilder,
    private loader: LoaderService,
    private canvasService: CanvasService,
    private readonly requestService: RequestService,
    private flashMessage: ToastService,
    private readonly toolbarService: ToolbarService,
    private readonly sessionManagerService: SessionManagerService,
    private translateService: TranslateService,
    public tabbingService: TabbingService,
  ) {}
  closeModal() {
    this.modalService.hide();
    localStorage.removeItem('emailAttachment');
    this.canvasService.setIsAnyInputPopupOpen(false);
    this.tabbingService.moveElement('fileActionListParent');
  }

  ngOnInit(): void {
    this.canvasService.setIsAnyInputPopupOpen(true);
    const preferences = this.sessionManagerService.getUserPreferences();
    let userData = localStorage.getItem('loggedInData');
    if (userData) {
      userData = JSON.parse(userData);
    }
    this.createForm(userData, preferences?.pdfDefaultEmail);
  }

  createForm(userData: any, defaultEmail = '') {
    this.formValues = this.fb.group(
      {
        email: [defaultEmail, [Validators.required, Validators.email, CustomValidator.validEmail]],
        subject: [
          '',
          [
            Validators.required,
            Validators.pattern(Constants.PATTERN.messageIncludedWithSpanishLetters),
            Validators.maxLength(Constants.CHARACTER_LIMIT.emailSubjectLength),
            CustomValidator.containOnlySpace,
          ],
        ],
        message: [
          '',
          [
            Validators.required,
            Validators.pattern(Constants.PATTERN.messageAdditionalCharsWithSpanishLetters),
            Validators.maxLength(Constants.CHARACTER_LIMIT.textareaMediumLength),
            CustomValidator.containOnlySpace,
          ],
        ],
      },
      {
        updateOn: 'blur',
      },
    );

    this.translateService.get(['emailPdfSubjectDefaultValue', 'emailPdfMessageDefaultValue'], { value: userData?.email }).subscribe(translations => {
      this.formValues.get('subject')?.setValue(translations['emailPdfSubjectDefaultValue']);
      this.formValues.get('message')?.setValue(translations['emailPdfMessageDefaultValue']);
    });
  }

  get f() {
    return this.formValues.controls;
  }

  sendPdf() {
    this.formValues.markAllAsTouched();
    if (!this.formValues.invalid) {
      this.loader.start();
      const data = {
        email: this.formValues.value.email,
        message: this.formValues.value.message,
        subject: this.formValues.value.subject,
        pdfData: localStorage.getItem('emailAttachment'),
        fileName: this.fileName,
      };
      this.requestService
        .post(`${Constants.ENDPOINTS.files}/send-pdf-email`, data)
        .pipe(
          timeout(15000),
          catchError(err => {
            const isOffline = err?.status === 0 || err?.name === 'TimeoutError';
            const message = isOffline
              ? 'Unable to email - no internet connection.'
              : err?.error?.errorMessage || err?.message || 'Something went wrong. Please try again.';
            this.flashMessage.showError(message);
            this.loader.stop();
            this.closeModal();
            return throwError(() => err);
          }),
        )
        .subscribe(() => {
          this.loader.stop();
          this.translateService.get('SUCCESS.fileSent').subscribe((translatedMessage: string) => {
            this.flashMessage.showSuccess(translatedMessage);
          });
          this.closeModal();
          this.toolbarService.openGoodJobPopUp();
        });
    }
  }

  ngOnDestroy(): void {
    this.canvasService.setIsAnyInputPopupOpen(false);
  }
}
